import { useEffect } from "react";
import Footer from "@/components/landing/layout/Footer";
import Header from "@/components/landing/layout/Header";

import { ShieldCheck, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";

type ClaimExample = { scenario: string; covered: string; reason: string };

interface CardAccident {
  image: string;
  Heading: string;
  para: string;
}
interface CardProtection {
  icon: React.ElementType;
  Heading: string;
  para: string;
}

const cardAccident: CardAccident[] = [
  {
    image: "/LovePangolinFAQ/Accidental1.webp",
    Heading: "Protection from Everyday Spills",
    para: "Accidental food and drink stains won’t ruin your apparel.",
  },
  {
    image: "/LovePangolinFAQ/Accidental2.webp",
    Heading: "Covered for Rips & Tears",
    para: "Accidental snags, pulls, or tears are protected.",
  },
  {
    image: "/LovePangolinFAQ/Accidental3.webp",
    Heading: "Safe from Heat Accidents",
    para: "Protection against accidental burns or iron marks.",
  },
  {
    image: "/LovePangolinFAQ/Accidental4.webp",
    Heading: "Covered Against Weather Damage",
    para: "Rain, wind, and accidental weather mishaps don’t leave you uncovered.",
  },
];

const cardProtection: CardProtection[] = [
  {
    icon: ShieldCheck,
    Heading: "Accidental damage",
    para: "Protect your apparel from accidental damage caused by spills, tears, color fades, and more.",
  },
  {
    icon: Heart,
    Heading: "Hassle-Free Claims",
    para: "File a claim with Protega’s 24/7 online support. Most claims are resolved in minutes.",
  },
];

const claimExamples: ClaimExample[] = [
  {
    scenario:
      "New Love Pangolin shirt stained permanently from accidental coffee spill at a café.",
    covered: "Yes",
    reason: "Single accidental liquid spill covered if not due to misuse.",
  },
  {
    scenario:
      "Love Pangolin jacket torn by pet running into you at home (unintentional).",
    covered: "Yes",
    reason: "Accidental pet-related damage covered.",
  },
  {
    scenario:
      "Love Pangolin pants ripped while travelling due to sudden unforeseen external accident (e.g., snag on airport bench).",
    covered: "Yes",
    reason: "Sudden unforeseen external accident covered.",
  },
  {
    scenario: "Love Pangolin T-shirt faded from regular washing over months.",
    covered: "No",
    reason: "Gradual wear and tear excluded.",
  },
  {
    scenario: "Apparel lost after successful delivery (stolen from residence).",
    covered: "No",
    reason: "Not covered under accidental damage plan.",
  },
  {
    scenario: "Bag damaged during attempted self-repair.",
    covered: "No",
    reason: "Unauthorized repairs void coverage.",
  },
];

const CONTENT = {
  title: "Protega Accidental Damage Protection Plan",
  subtitle: "Terms and Conditions",
  introduction: [
    "1.1. The Protega Product Protection Plan (“Plan”) provides customers with comprehensive coverage for eligible Love Pangolin products, including apparel, accessories, bags, and related items. It covers accidental damage occurring during normal domestic use.",
    "1.2. This Plan is an optional, paid service contract. It does not replace or extend the manufacturer’s warranty and is not an insurance policy.",
    "1.3. Coverage includes accidental damage such as tears, spills, pet damage, accidental drops, or sudden impacts.",
    "1.4. Administered by EmbedMike India Pvt. Ltd. (“Protega”), which manages claims, repairs, and replacements.",
    "1.5. Merchant: Love Pangolin",
    "1.6. Governing Law: Laws of India",
    "1.7. Jurisdiction: Exclusive jurisdiction of Mumbai courts",
  ],
  customerResponsibilities: [
    "Retain your purchase invoice and Protection Plan certificate.",
    "File claims as soon as practicable after the damage incident.",
    "Avoid unauthorized repairs or modifications, which may void coverage.",
    "Maintain products per the care instructions provided by Love Pangolin.",
    "Handle products responsibly to prevent damages outside accidental events.",
  ],
  howToFileClaim: {
    contactMethods: [
      "Email: support@protega.ai",
      "Web: www.protega.ai/customer-login",
    ],
    requiredDocuments: [
      "Protection Plan certificate and purchase invoice",
      "Photos or videos clearly showing the damaged areas",
      "Detailed description of the incident (when and how it occurred)",
    ],
    protegaRemedies: [
      "Repair using genuine parts or professional services",
      "Replacement with same or comparable product",
      "Voucher/credit equal to the invoice value if repair/replacement is not feasible",
    ],
    claimLimit: [
      "Only one valid claim per product under the Plan",
      "Claims must be filed as soon as practicable after the incident",
    ],
  },
  definitions: [
    {
      term: "Customer",
      meaning: "Person purchasing a Love Pangolin product with Protection Plan",
    },
    { term: "Merchant", meaning: "Love Pangolin" },
    {
      term: "Protega",
      meaning: "EmbedMike India Pvt. Ltd., Plan administrator",
    },
    {
      term: "Product",
      meaning: "Eligible Love Pangolin item (apparel, bag, accessory, home décor)",
    },
    {
      term: "Accidental Damage",
      meaning:
        "Sudden, unintended events causing physical damage (tears, spills, pet damage, drops, impacts)",
    },
    {
      term: "Policy Period",
      meaning: "12 or 24 months from delivery date, based on selected Plan",
    },
    { term: "Sum Insured", meaning: "Invoice value of the product" },
    {
      term: "Claim Limit",
      meaning: "One valid claim per product, up to Sum Insured",
    },
  ] as { term: string; meaning: string }[],
  eligibility: [
    "Coverage applies only to products purchased directly from Love Pangolin website with Product Protection selected at checkout.",
    "Coverage begins on delivery date and lasts for the chosen Policy Period.",
    "Valid within India only.",
  ],
  whatIsCovered: [
    "Accidental tearing, punctures, crushing, or breakage of the product",
    "Liquid spills or stains affecting product usability",
    "Pet damage (scratches, chewing, tearing)",
    "Accidental drops or impacts causing visible damage",
    "Non-consumable accessories sold with the product (e.g., detachable straps, add-ons)",
    "Coverage is single-event per product, up to invoice value",
  ],
  exclusions: [
    "Manufacturer defects or faults (covered under warranty)",
    "Normal wear & tear, fading, fraying, or minor stains from repeated use",
    "Cosmetic scratches or dents that do not affect functionality",
    "Pre-existing damage prior to Plan coverage start date",
    "Intentional damage, misuse, mishandling, or gross negligence",
    "Use outside normal domestic settings",
    "Consumables (filters, pads, or accessories) unless damaged during a covered event",
    "Unauthorized repairs, modifications, or installation of non-genuine parts",
    "Any loss, damage, or deterioration caused by exposure to fire, flame, excessive heat, smoke, or burns of any nature (including but not limited to cigarette burns)",
    "Damage due to failure to follow product care instructions",
    "Claims reported after Policy Period or without valid reason",
  ],
  serviceProcedures: [
    "Protega evaluates each claim thoroughly, ensuring accidental damage verification",
    "Repairs and replacements are conducted only by authorized service providers using genuine parts",
    "Voucher/credit issued when repair or replacement is not feasible",
    "Maximum liability is capped at the invoice value of the product",
    "Plan ends once a claim is resolved",
    "Protega may request submission of damaged product or parts for inspection",
    "Subrogation rights are retained to recover costs from responsible third parties",
    "Protega maintains clear and transparent communication throughout the claim process",
  ],
  cancellations: [
    "Customers may cancel within 30 days of purchase if no claim has been filed for a full refund",
    "After 30 days or post-claim, Plan is non-cancellable and non-refundable",
    "Protega may cancel in cases of fraud, misrepresentation, or non-payment",
    "Customers are encouraged to report issues promptly to maintain claim eligibility",
  ],
  conditions: [
    "Renewal is not available; a new Plan must be purchased after expiry",
    "Non-transferable; linked to original purchaser",
    "Valid only within India",
    "Customers must maintain products per manufacturer instructions",
    "Protega reserves rights to recover costs from third parties responsible for damage",
    "Continuous guidance provided to customers to ensure clear understanding of coverage",
  ],
  legalDisclosures: [
    "Disputes resolved via arbitration under Arbitration & Conciliation Act, 1996",
    "Plan and purchase confirmation constitute the entire agreement",
    "Severability ensures invalid clauses do not affect other terms",
    "Customer data processed as per Protega Privacy Policy",
    "Protega and Love Pangolin are not liable for indirect, incidental, or consequential losses",
    "Protega may amend policies as needed, following regulatory requirements",
  ],
  claimExamples,
  faqs: [
    {
      q: "Is this Plan the same as manufacturer warranty?",
      a: "No. Manufacturer warranty covers defects; Plan covers accidental damage.",
    },
    {
      q: "How long am I covered?",
      a: "Coverage starts on delivery date and lasts 12 or 24 months.",
    },
    {
      q: "How many claims can I file?",
      a: "One valid claim per product, up to invoice value.",
    },
    {
      q: "What documents are required to file a claim?",
      a: "Purchase invoice, Plan certificate, and photos/videos showing damage.",
    },
    {
      q: "Can I cancel the Plan?",
      a: "Yes, within 30 days if no claims. After that, Plan is non-refundable.",
    },
    {
      q: "Does the Plan cover accessories or add-ons?",
      a: "Only if they are damaged in a covered accidental event.",
    },
    {
      q: "Can I transfer my Plan?",
      a: "No, Plans are non-transferable and linked to original purchaser.",
    },
    {
      q: "What if my product is out of stock during replacement?",
      a: "You will receive a gift voucher up to invoice value.",
    },
    {
      q: "Is original packaging mandatory?",
      a: "Not mandatory, but photos/videos showing product condition are required.",
    },
    {
      q: "What if I misplace my invoice?",
      a: "Proof of purchase is mandatory for claim processing.",
    },
    {
      q: "Can I claim if damage occurred after moving to a new house?",
      a: "Only if damage occurred under normal household use. Non-domestic locations excluded.",
    },
    {
      q: "How soon will my claim be resolved?",
      a: "Most claims are resolved within 5–7 working days after submission and verification.",
    },
  ],
  serviceCommitment: [
    "Policy certificate emailed within 24 hours of purchase",
    "Claims acknowledged within 24 hours",
    "Inspection and verification within 5–7 working days",
    "Pickup of damaged product arranged within 48 hours post-approval",
    "Repair/replacement or gift voucher issued within 5 business days",
    "Dedicated support team available at support@protega.ai",
  ],
  contact: {
    protegaEmail: "support@protega.ai",
    protegaWeb: "https://www.protega.ai/customer-login",
    lovePangolinEmail: "support@lovepangolin.com",
  },
};

const LovePangolinTerms: React.FC = () => {
  return (
    <section className="tw-bg-white tw-font-mori tw-max-w-5xl tw-mx-auto tw-px-6 tw-py-16 tw-text-[#1C1E21] tw-leading-relaxed">
      <h1 className="md:tw-text-4xl tw-text-3xl tw-font-bold tw-mb-2">
        {CONTENT.title}
      </h1>
      <p className="tw-text-lg tw-text-gray-500 tw-mb-12">{CONTENT.subtitle}</p>

      <div className="tw-space-y-12">
        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">1. Plan Introduction</h2>
          {CONTENT.introduction.map((p, i) => (
            <p key={i} className="tw-mb-3 tw-text-base">
              {p}
            </p>
          ))}
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">
            2. Customer Responsibilities
          </h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.customerResponsibilities.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">
            3. How to File a Claim
          </h2>
          <div className="tw-space-y-6">
            <div>
              <p className="tw-font-bold tw-mb-2">Contact Methods:</p>
              <ul className="tw-list-disc tw-list-inside tw-text-base">
                {CONTENT.howToFileClaim.contactMethods.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="tw-font-bold tw-mb-2">Required Documents:</p>
              <ul className="tw-list-disc tw-list-inside tw-text-base">
                {CONTENT.howToFileClaim.requiredDocuments.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="tw-font-bold tw-mb-2">
                Protega Remedies (at discretion):
              </p>
              <ul className="tw-list-disc tw-list-inside tw-text-base">
                {CONTENT.howToFileClaim.protegaRemedies.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="tw-font-bold tw-mb-2">Claim Limit:</p>
              <ul className="tw-list-disc tw-list-inside tw-text-base">
                {CONTENT.howToFileClaim.claimLimit.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">4. Definitions</h2>
          <div className="tw-overflow-x-auto tw-rounded-xl tw-border tw-border-gray-200">
            <table className="tw-w-full tw-text-base tw-table-fixed">
              <thead className="tw-bg-gray-50">
                <tr>
                  <th className="tw-p-4 tw-border-r tw-text-left tw-w-1/3 tw-font-bold">
                    Term
                  </th>
                  <th className="tw-p-4 tw-text-left tw-font-bold">Definition</th>
                </tr>
              </thead>
              <tbody>
                {CONTENT.definitions.map((d, i) => (
                  <tr
                    key={i}
                    className={i % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50/50"}
                  >
                    <td className="tw-p-4 tw-border-r tw-font-medium">
                      {d.term}
                    </td>
                    <td className="tw-p-4">{d.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">5. Eligibility</h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.eligibility.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">6. What is Covered</h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.whatIsCovered.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">7. What is Not Covered</h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.exclusions.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">
            8. Service Procedures & Limit of Liability
          </h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.serviceProcedures.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">9. Cancellation</h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.cancellations.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">10. Conditions</h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.conditions.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">11. Legal Disclosures</h2>
          <ul className="tw-list-inside tw-text-base tw-space-y-2 tw-list-disc">
            {CONTENT.legalDisclosures.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">
            12. Filing a Claim – Examples
          </h2>
          <div className="tw-overflow-x-auto tw-rounded-xl tw-border tw-border-gray-200">
            <table className="tw-w-full tw-text-base tw-table-fixed">
              <thead className="tw-bg-gray-50">
                <tr>
                  <th className="tw-p-4 tw-border-r tw-text-left md:tw-w-1/2 tw-font-bold">
                    Scenario
                  </th>
                  <th className="tw-p-4 tw-border-r tw-text-left tw-font-bold">
                    Covered?
                  </th>
                  <th className="tw-p-4 tw-text-left tw-font-bold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {CONTENT.claimExamples.map((ex, i) => (
                  <tr
                    key={i}
                    className={i % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50/50"}
                  >
                    <td className="tw-p-4 tw-border-r">{ex.scenario}</td>
                    <td className="tw-p-4 tw-border-r tw-font-medium">
                      {ex.covered}
                    </td>
                    <td className="tw-p-4">{ex.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-6">13. FAQ</h2>
          <div className="tw-space-y-6">
            {CONTENT.faqs.map((f, i) => (
              <div key={i} className="tw-bg-gray-50 tw-p-6 tw-rounded-xl">
                <p className="tw-font-bold tw-text-lg tw-mb-2">
                  Q{i + 1}: {f.q}
                </p>
                <p className="tw-text-gray-700">A: {f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-4">
            14. Service Commitment
          </h2>
          <ul className="tw-list-disc tw-list-inside tw-text-base tw-space-y-2">
            {CONTENT.serviceCommitment.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="tw-font-bold tw-text-xl tw-mb-6">15. Contact Us</h2>
          <div className="tw-bg-gray-900 tw-text-white tw-p-8 tw-rounded-2xl tw-space-y-6">
            <div>
              <p className="tw-font-bold tw-text-lg">Protega Claims & Support:</p>
              <p className="tw-text-gray-400">
                Email:{" "}
                <a
                  className="tw-text-white tw-underline hover:tw-no-underline"
                  href={`mailto:${CONTENT.contact.protegaEmail}`}
                >
                  {CONTENT.contact.protegaEmail}
                </a>
              </p>
              <p className="tw-text-gray-400">
                Web:{" "}
                <a
                  className="tw-text-white tw-underline hover:tw-no-underline"
                  href={CONTENT.contact.protegaWeb}
                >
                  {CONTENT.contact.protegaWeb}
                </a>
              </p>
            </div>
            <div className="tw-pt-4 tw-border-t tw-border-gray-800">
              <p className="tw-font-bold tw-text-lg">Love Pangolin Support:</p>
              <p className="tw-text-gray-400">
                Email:{" "}
                <a
                  className="tw-text-white tw-underline hover:tw-no-underline"
                  href={`mailto:${CONTENT.contact.lovePangolinEmail}`}
                >
                  {CONTENT.contact.lovePangolinEmail}
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};

const LovePangolinCare: React.FC = () => {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-black tw-text-white tw-font-mori">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        {/* HERO */}
        <section className="tw-relative tw-w-[95%] tw-mx-auto">
          <div className="tw-relative tw-h-[85vh] tw-bg-black tw-rounded-3xl tw-overflow-hidden tw-flex tw-items-center">
            <img
              src="/LovePangolinFAQ/Hero.webp"
              alt="Hero Image"
              className="tw-absolute tw-inset-0 tw-object-cover tw-opacity-60 tw-w-full tw-h-full -tw-z-10"
            />
            <div className="tw-relative tw-z-10 tw-px-10 lg:tw-px-24 tw-w-full">
              <div className="tw-max-w-4xl">
                <img
                  src="/desktopLogo.png"
                  alt="Logo"
                  className="tw-h-12 tw-mb-10 lg:tw-h-16"
                />
                <h1 className="tw-text-5xl md:tw-text-8xl tw-font-bold tw-leading-tight tw-mb-10">
                  Enjoy Peace of Mind
                </h1>
                <div className="tw-flex tw-flex-col tw-gap-8 tw-items-start">
                  <span className="tw-text-2xl md:tw-text-3xl tw-font-light tw-text-gray-300">
                    Simple, hassle-free product protection for the products you
                    love.
                  </span>
                  <a
                    href="https://www.lovepangolin.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="tw-bg-white tw-text-black hover:tw-bg-gray-200 tw-transition-all tw-px-10 tw-py-4 tw-text-xl tw-font-bold tw-rounded-full"
                  >
                    Shop Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Logos */}
        <div className="tw-flex tw-items-center tw-py-24 tw-justify-center tw-gap-12">
          <img
            src="/LovePangolinFAQ/logo.png"
            className="tw-h-16 tw-w-auto"
            alt="Love Pangolin Logo"
          />
          <div className="tw-h-12 tw-w-px tw-bg-gray-800" />
          <img src="/icons/logo.png" className="tw-h-12 tw-w-auto" alt="Protega Logo" />
        </div>

        {/* Accidental Damage */}
        <section className="tw-bg-white tw-text-black tw-py-32">
          <div className="tw-w-[90%] tw-mx-auto tw-text-center">
            <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-mb-20">
              Accidental Damage Protection
            </h2>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-12">
              {cardAccident.map((item, count) => (
                <div
                  key={count}
                  className="tw-group tw-flex tw-flex-col tw-items-center"
                >
                  <div className="tw-w-full tw-aspect-square tw-mb-8 tw-overflow-hidden tw-rounded-2xl">
                    <img
                      src={item.image}
                      alt={item.Heading}
                      className="tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-110 tw-transition-transform tw-duration-500"
                    />
                  </div>
                  <h3 className="tw-text-xl tw-font-bold tw-mb-4">
                    {item.Heading}
                  </h3>
                  <p className="tw-text-gray-600">{item.para}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Protection plan coverage */}
        <section className="tw-bg-gray-50 tw-text-black tw-py-32">
          <div className="tw-w-[90%] tw-mx-auto tw-text-center">
            <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-mb-20">
              Protection plan coverage*
            </h2>
            <div className="tw-flex tw-flex-wrap tw-gap-12 tw-justify-center">
              {cardProtection.map((item, index) => (
                <div
                  key={index}
                  className="tw-bg-white tw-p-10 tw-rounded-3xl tw-shadow-xl tw-shadow-black/5 tw-flex tw-items-start tw-max-w-md tw-gap-6 tw-text-left"
                >
                  <div className="tw-bg-black tw-p-4 tw-rounded-2xl">
                    <item.icon className="tw-h-8 tw-w-8 tw-text-white" />
                  </div>
                  <div>
                    <h3 className="tw-text-xl tw-font-bold tw-mb-3">
                      {item.Heading}
                    </h3>
                    <p className="tw-text-gray-600">{item.para}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* From delivery to daily use */}
        <section className="tw-bg-white tw-text-black tw-py-32 tw-border-b tw-border-gray-100">
          <div className="tw-max-w-4xl tw-mx-auto tw-px-10 tw-text-center">
            <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-mb-10">
              From delivery to daily use:
            </h2>
            <div className="tw-bg-black tw-text-white tw-p-10 tw-rounded-3xl tw-shadow-2xl">
              <p className="tw-text-2xl md:tw-text-3xl tw-font-light">
                <span className="tw-text-green-400 tw-font-bold tw-mr-2">✓</span>{" "}
                <strong>Product protection:</strong> No sweat repairs or
                replacements in case of accidental damage depending on your
                product.
              </p>
            </div>
          </div>
        </section>

        {/* Why add Protega */}
        <section className="tw-bg-white tw-text-black tw-py-32">
          <div className="tw-max-w-4xl tw-mx-auto tw-px-10 tw-text-center">
            <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-mb-12">
              Why add Protega?
            </h2>
            <p className="tw-text-xl md:tw-text-2xl tw-leading-relaxed tw-text-gray-600">
              Protega provides coverage for your apparel against accidental
              damage caused by spills, tears, snags, and more. Whether it’s an
              unexpected coffee spill on your shirt or a ripped hem from a busy
              commute, accidental damage coverage has you protected.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="tw-bg-black tw-text-white tw-py-32 tw-overflow-hidden">
          <div className="tw-w-[90%] tw-mx-auto tw-grid lg:tw-grid-cols-2 tw-gap-16 tw-items-center">
            <div className="tw-space-y-10">
              <h2 className="tw-text-5xl md:tw-text-7xl tw-font-bold">
                How it works
              </h2>
              <div className="tw-space-y-6">
                <h3 className="tw-text-3xl tw-font-bold tw-text-gray-200">
                  Easy claims, great service
                </h3>
                <p className="tw-text-xl tw-text-gray-400 tw-leading-relaxed">
                  Filing a claim is simple. They’ll process your claim in
                  minutes and once approved, they’ll mail you details on your
                  replacement.
                </p>
              </div>
              <Link
                to={"/customer/claims"}
                className="tw-inline-block tw-bg-white tw-text-black hover:tw-bg-gray-200 tw-transition-all tw-px-10 tw-py-4 tw-text-xl tw-font-bold tw-rounded-full"
              >
                Start a Claim
              </Link>
            </div>
            <div className="tw-relative">
              <div className="tw-absolute -tw-inset-4 tw-bg-white/10 tw-blur-3xl tw-rounded-full" />
              <img
                src="/LovePangolinFAQ/HowItWorks.png"
                className="tw-relative tw-w-full tw-rounded-3xl tw-shadow-2xl"
                alt="How It Works"
              />
            </div>
          </div>
        </section>

        <LovePangolinTerms />

        <Footer />
      </main>
    </div>
  );
};


export default LovePangolinCare;
